import { ToggleOff, ToggleOn } from '@mui/icons-material';
import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from '@mui/material';
import { ChangeEvent, FC, useState } from 'react';
import { TrashIcon } from '../../../../../assets/icons';
import {
  ConfirmDialog,
  ConfirmDialogOptions,
  EmptyTable,
  FilterTableMenu,
  StatusItem,
} from '../../../../../components';
import { useLoading, useNotify } from '../../../../../hooks';
import { PeriodService } from '../../../../../services';
import { TablePaginatorOptions } from '../../../../../utils/consts';
import { DirectionOptions } from '../../../../../utils/enums';
import { PeriodoDTO } from '../../../../../utils/models';
import { PeriodTableColumn } from '../../enums/PeriodTableColumn';
import { usePeriods } from '../../hooks/usePeriods';
import { PeriodTableActions } from '../PeriodTableActions/PeriodTableActions';

interface Props {
  onReloadData: () => void;
  onTableFilter: (
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    asc: string,
  ) => void;
}

export const PeriodTable: FC<Props> = ({ onReloadData, onTableFilter }) => {
  const periodService = new PeriodService();
  const theme = useTheme();
  const palette = theme.palette;
  const { notify, setNotify } = useNotify();
  const { setLoading } = useLoading();
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogOptions>({
    open: false,
    title: '',
    subTitle: '',
    confirmText: '',
    cancelText: '',
    onConfirm: () => null,
  });

  const {
    changeStatus,
    periodList,
    paginatorOptions,
    page,
    changePage,
    rowsPerPage,
    changeRowsPerPage,
    changeModal,
    modalRecord,
    orderBy,
    dir,
    setOrder,
  } = usePeriods();

  const onAlert = async (
    period: PeriodoDTO,
    type: 'edit' | 'delete',
    subTitle: string,
  ) => {
    setConfirmDialog({
      title:
        type === 'edit'
          ? 'Modificar el status del periodo'
          : 'Eliminar el periodo',
      subTitle,
      confirmText:
        type === 'edit'
          ? period.activo
            ? 'Sí, desactivar'
            : 'Sí, activar'
          : 'Sí, eliminar',
      cancelText: 'No, Cancelar',
      open: true,
      startIcon:
        type === 'edit' ? (
          period.activo ? (
            <ToggleOn sx={{ color: 'grayCustom.main' }} />
          ) : (
            <ToggleOff sx={{ color: 'grayCustom.main' }} />
          )
        ) : (
          <TrashIcon fill={palette.grayCustom.main} />
        ),
      endIcon:
        type === 'edit' ? (
          <StatusItem
            status={!period.activo}
            bgColor={period.activo ? palette.grayCustom.darker : null}
            bgText={period.activo ? palette.grayCustom.main : null}
          />
        ) : null,
      onConfirm: () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        switch (type) {
          case 'delete':
            deletePeriod(period);
            break;
          case 'edit':
            updateStatusPeriod(period, !period.activo);
            break;
        }
      },
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    const value = newPage + 1;
    changePage(value);
    onTableFilter(rowsPerPage, value, orderBy, dir);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    changeRowsPerPage(value);
    const valuePage = 1;
    changePage(valuePage);
    onTableFilter(value, valuePage, orderBy, dir);
  };

  const updateStatusPeriod = async (period: PeriodoDTO, status: boolean) => {
    setLoading(true);
    await periodService
      .updateStatusPeriod(
        period.id,
        period.nivelId,
        period.sufijoPrograma,
        period.esSemestreEmpresarial,
        status,
      )
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          setNotify({
            ...notify,
            open: true,
            message: 'Periodo actualizado correctamente',
            type: 'success',
          });
          onReloadData();
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const deletePeriod = async (period: PeriodoDTO) => {
    setLoading(true);
    await periodService
      .deletePeriod(
        period.id,
        period.nivelId,
        period.sufijoPrograma,
        period.esSemestreEmpresarial,
      )
      .then((response) => {
        setLoading(false);
        const res = response.data;
        if (res.success) {
          setNotify({
            ...notify,
            open: true,
            message: 'Se eliminó el periodo correctamente.',
            type: 'success',
          });
          onReloadData();
        } else {
          setNotify({
            ...notify,
            open: true,
            message: res.message,
            type: 'error',
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onOrderUpward = (columnName: PeriodTableColumn) => {
    setOrder(columnName, DirectionOptions.ASC);
    onTableFilter(rowsPerPage, page, columnName, DirectionOptions.ASC);
  };

  const onOrderFalling = (columnName: PeriodTableColumn) => {
    setOrder(columnName, DirectionOptions.DESC);
    onTableFilter(rowsPerPage, page, columnName, DirectionOptions.DESC);
  };

  return (
    <>
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell variant="head">
                Clave{' '}
                <FilterTableMenu
                  onOrderUpward={() => onOrderUpward(PeriodTableColumn.CLAVE)}
                  onOrderFalling={() => onOrderFalling(PeriodTableColumn.CLAVE)}
                />
              </TableCell>
              <TableCell variant="head" sx={{ minWidth: '200px' }}>
                Descripción{' '}
                <FilterTableMenu
                  onOrderUpward={() =>
                    onOrderUpward(PeriodTableColumn.DESCRIPCION)
                  }
                  onOrderFalling={() =>
                    onOrderFalling(PeriodTableColumn.DESCRIPCION)
                  }
                />
              </TableCell>
              <TableCell variant="head" sx={{ minWidth: '200px' }}>
                Nivel{' '}
                <FilterTableMenu
                  onOrderUpward={() => onOrderUpward(PeriodTableColumn.NIVEL)}
                  onOrderFalling={() => onOrderFalling(PeriodTableColumn.NIVEL)}
                />
              </TableCell>
              <TableCell variant="head" sx={{ minWidth: '200px' }}>
                Sufijo de Programas{' '}
                <FilterTableMenu
                  onOrderUpward={() =>
                    onOrderUpward(PeriodTableColumn.SUFIJO_PROGRAMA)
                  }
                  onOrderFalling={() =>
                    onOrderFalling(PeriodTableColumn.SUFIJO_PROGRAMA)
                  }
                />
              </TableCell>
              <TableCell variant="head" sx={{ minWidth: '200px' }}>
                Semestre empresarial{' '}
                <FilterTableMenu
                  onOrderUpward={() =>
                    onOrderUpward(PeriodTableColumn.SUFIJO_PROGRAMA)
                  }
                  onOrderFalling={() =>
                    onOrderFalling(PeriodTableColumn.SUFIJO_PROGRAMA)
                  }
                />
              </TableCell>
              <TableCell variant="head">
                Status{' '}
                <FilterTableMenu
                  onOrderUpward={() => onOrderUpward(PeriodTableColumn.STATUS)}
                  onOrderFalling={() =>
                    onOrderFalling(PeriodTableColumn.STATUS)
                  }
                />
              </TableCell>
              <TableCell variant="head" align="center" sx={{ pt: 1 }}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periodList.map((row) => (
              <TableRow
                key={`row-${row.id}-${row.nivelId}-${row.sufijoPrograma}-${row.esSemestreEmpresarial}`}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.nombre}</TableCell>
                <TableCell>{row.nombreNivel}</TableCell>
                <TableCell>{row.sufijoPrograma}</TableCell>
                <TableCell>{row.esSemestreEmpresarial ? 'SI' : 'NO'}</TableCell>
                <TableCell>
                  {' '}
                  <StatusItem status={row.activo} />{' '}
                </TableCell>

                <TableCell align="center">
                  <PeriodTableActions
                    period={row}
                    onEdit={() =>
                      changeModal({
                        ...modalRecord,
                        show: true,
                        type: 'edit',
                        id: row.id,
                        periodEdit: row,
                      })
                    }
                    onChangeStatus={() =>
                      onAlert(
                        row,
                        'edit',
                        `¿Deseas cambiar el status del periodo a “${
                          row.activo ? 'INACTIVO' : 'ACTIVO'
                        }”?`,
                      )
                    }
                    onDelete={() =>
                      onAlert(
                        row,
                        'delete',
                        `¿Deseas eliminar el periodo “${row.nombre}”?`,
                      )
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <EmptyTable elements={periodList} />
      <Divider />
      <TablePagination
        rowsPerPageOptions={TablePaginatorOptions.rowsPerPageOptions}
        component="div"
        count={paginatorOptions.totalCount}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        SelectProps={{
          inputProps: {
            'aria-label': 'Filas por página',
          },
          native: true,
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};
